import { Alert, Box, Button, Paper, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FilterBar } from "../components/filters/FilterBar";
import { BaselineSelector } from "../components/filters/BaselineSelector";
import { RefreshControls } from "../components/filters/RefreshControls";
import { ServiceInstancesFilter } from "../components/filters/ServiceInstancesFilter";
import { ServiceNamesFilter } from "../components/filters/ServiceNamesFilter";
import { TimeRangeFilterBar } from "../components/filters/TimeRangeFilterBar";
import { Panel } from "../components/layout/Panel";
import { CompGrid, type Column } from "../components/grid/CompGrid";
import { createTraceIdColumn } from "../components/grid/columns/traceColumns";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { Bar, BarChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import type { Span, SpanStatisticsBaselineWindow } from "../api/client";
import { useMetadata } from "../hooks/useMetadata";
import { useSpans } from "../hooks/useSpans";
import { useSpanStatistics } from "../hooks/useSpanStatistics";
import {
  useServiceInstancesFilterFromQuery,
  useServiceNamesFilterFromQuery,
  useTimeRangeFromQuery,
} from "../hooks/useRouteParams";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { formatDuration, formatStatusCode } from "../utils/formatting";
import { BaselineTooltip } from "../components/stats/BaselineTooltip";
import { IdLink, SpanIdLink } from "../components/links/IdLink";

function formatDurationBucket(loMs: number, hiMs: number): string {
  // Keep the histogram binning as-is, but display "sane" boundaries:
  // - no decimals for ms
  // - round the lower bound down to a 10ms block for readability
  if (hiMs < 1000) {
    const lo = Math.max(0, Math.floor(loMs / 10) * 10);
    const hi = Math.max(lo, Math.ceil(hiMs));
    return `${lo}–${hi} ms`;
  }

  // Fallback for larger ranges: reuse existing duration formatter.
  // (This will include units on both sides, e.g. "1.23s–2.50s")
  return `${formatDuration(loMs)}–${formatDuration(hiMs)}`;
}

function buildHistogram(values: number[], buckets = 18) {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) {
    return [{ bucket: `${formatDuration(min)}`, count: values.length }];
  }

  const step = (max - min) / buckets;
  const counts = new Array(buckets).fill(0) as number[];
  for (const v of values) {
    const idx = Math.min(buckets - 1, Math.max(0, Math.floor((v - min) / step)));
    counts[idx] = (counts[idx] ?? 0) + 1;
  }

  return counts.map((count, i) => {
    const lo = min + i * step;
    const hi = i === buckets - 1 ? max : min + (i + 1) * step;
    return { bucket: formatDurationBucket(lo, hi), count };
  });
}

export function PageSpanPerformance() {
  const navigate = useNavigate();
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { spanName } = useParams<{ spanName: string }>();
  const { range } = useTimeRangeFromQuery();
  const [searchParams] = useSearchParams();
  const [baseline, setBaseline] = useState<SpanStatisticsBaselineWindow>("24h");
  const theme = useTheme();

  const [refreshToken, setRefreshToken] = useState(0);

  const { serviceNames } = useServiceNamesFilterFromQuery();
  const { serviceInstances } = useServiceInstancesFilterFromQuery();

  const selectedServiceName = serviceNames[0] ?? undefined;
  const selectedServiceInstance = serviceInstances[0] ?? undefined;

  const { metadata } = useMetadata(serviceSlug);
  const metadataFormatted = useMemo(() => {
    if (!metadata) return { services: [], instances: [] };
    return {
      services: metadata.services.map((s) => s.service_name),
      instances: metadata.services.flatMap((s) => s.instances.map((i) => i.instance_id)),
    };
  }, [metadata]);

  const {
    spans,
    loading: spansLoading,
    error: spansError,
    isFetching: spansFetching,
  } = useSpans({
    serviceSlug,
    timeRange: range,
    serviceNames,
    serviceInstances,
    enabled: !!serviceSlug,
    limit: 500,
    refreshToken,
  });

  const {
    stats,
    loading: statsLoading,
    error: statsError,
    isFetching: statsFetching,
  } = useSpanStatistics({
    serviceSlug,
    timeRange: range,
    serviceName: selectedServiceName,
    serviceInstance: selectedServiceInstance,
    spanName: spanName ?? undefined,
    baseline,
    enabled: !!serviceSlug && !!spanName,
    limit: 50,
    refreshToken,
  });
  const isFetching = spansFetching || statsFetching;

  const filteredSpans = useMemo(() => {
    if (!spanName) return [];
    return spans.filter((s) => s.span_name === spanName);
  }, [spans, spanName]);

  const durationsMs = useMemo(() => {
    return filteredSpans
      .map((s) => new Date(s.end_time).getTime() - new Date(s.start_time).getTime())
      .filter((v) => Number.isFinite(v) && v >= 0);
  }, [filteredSpans]);

  const histogram = useMemo(() => buildHistogram(durationsMs, 18), [durationsMs]);

  const statusBreakdown = useMemo(() => {
    const map = new Map<number, number>();
    for (const s of filteredSpans) {
      map.set(s.status_code, (map.get(s.status_code) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [filteredSpans]);

  const stat = stats[0];

  const baselineP95 = stat?.baseline?.p95_duration;

  const columns: Column<Span>[] = [
    {
      id: "span_id",
      label: "Span ID",
      sortable: true,
      sortValue: (s) => s.span_id,
      render: (s) => {
        const params = new URLSearchParams();
        if (searchParams.has("range_from")) {
          params.set("range_from", searchParams.get("range_from") ?? "");
        }
        if (searchParams.has("range_stop")) {
          params.set("range_stop", searchParams.get("range_stop") ?? "");
        }
        return (
          <SpanIdLink
            serviceSlug={serviceSlug}
            spanId={s.span_id}
            truncateAt={0}
            range={range}
            rangeSearchParams={params}
          />
        );
      },
    },
    {
      id: "span_name",
      label: "Span Name",
      sortable: true,
      sortValue: (s) => s.span_name,
      render: (s) => {
        if (!serviceSlug) return <Typography variant="body2">{s.span_name}</Typography>;
        const params = new URLSearchParams();
        params.set("service_name", s.service_name);
        if (s.service_instance) params.set("service_instance", s.service_instance);
        if (searchParams.has("range_from")) {
          params.set("range_from", searchParams.get("range_from") ?? "");
        }
        if (searchParams.has("range_stop")) {
          params.set("range_stop", searchParams.get("range_stop") ?? "");
        }
        const to = `/services/${encodeURIComponent(serviceSlug)}/spans/perf/${encodeURIComponent(s.span_name)}?${params.toString()}`;
        return <IdLink to={to} value={s.span_name} truncateAt={0} />;
      },
    },
    {
      id: "duration",
      label: `Duration vs baseline ${baseline}`,
      sortable: true,
      sortValue: (s) => new Date(s.end_time).getTime() - new Date(s.start_time).getTime(),
      render: (s) => {
        const durationMs = new Date(s.end_time).getTime() - new Date(s.start_time).getTime();
        let deltaNode: React.ReactNode = null;
        if (
          baselineP95 != null &&
          Number.isFinite(baselineP95) &&
          baselineP95 > 0 &&
          Number.isFinite(durationMs)
        ) {
          const pct = ((durationMs - baselineP95) / baselineP95) * 100;
          const sign = pct >= 0 ? "+" : "";
          const text = `${sign}${pct.toFixed(0)}%`;
          deltaNode = (
            <Typography
              component="span"
              variant="caption"
              sx={{
                ml: 0.5,
                color: pct > 0 ? "error.main" : pct < 0 ? "success.main" : "text.secondary",
              }}
            >
              {text}
            </Typography>
          );
        }
        const cell = (
          <Typography component="span" variant="body2">
            {formatDuration(durationMs)}
            {deltaNode}
          </Typography>
        );
        return (
          <BaselineTooltip baseline={stat?.baseline} showArrow>
            {cell}
          </BaselineTooltip>
        );
      },
    },
    {
      id: "start_time",
      label: "Start Time",
      sortable: true,
      sortValue: (s) => new Date(s.start_time).getTime(),
      render: (s) => (
        <Typography variant="body2">{new Date(s.start_time).toLocaleString()}</Typography>
      ),
    },
    createTraceIdColumn<Span>({
      serviceSlug: serviceSlug ?? "",
      getTraceId: (s) => s.trace_id,
      truncateAt: 16,
    }),
    {
      id: "service",
      label: "Service",
      sortable: true,
      sortValue: (s) => s.service_name,
      render: (s) => (
        <Typography variant="body2">
          {s.service_name}
          {s.service_instance ? ` (${s.service_instance})` : ""}
        </Typography>
      ),
    },
  ];

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  if (!spanName) {
    return (
      <Box>
        <PageErrorState message="Missing spanName in URL." />
      </Box>
    );
  }

  const loading = spansLoading || statsLoading;
  const error = spansError || statsError;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel title={`Span performance: ${spanName}`} subtitle="Aggregated span duration statistics">
        <FilterBar>
          <TimeRangeFilterBar />
          <ServiceNamesFilter options={metadataFormatted.services} />
          <ServiceInstancesFilter options={metadataFormatted.instances} />
          <BaselineSelector value={baseline} onChange={setBaseline} />
          <Box data-filterbar-wide="true" sx={{ maxWidth: "none", minWidth: "auto" }}>
            <RefreshControls setRefreshToken={setRefreshToken} isFetching={isFetching} />
          </Box>
        </FilterBar>
        {error ? <PageErrorState message={error} sx={{ mt: 1.5 }} /> : null}
        {loading ? <PageLoadingState message="Loading span performance…" sx={{ mt: 1.5 }} /> : null}
      </Panel>

      <Panel
        title="Distribution (approx.)"
        subtitle={`Based on ${durationsMs.length} spans in current range`}
      >
        {histogram.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No data to build histogram.
          </Typography>
        ) : (
          <Box sx={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={histogram.map((b) => ({ name: b.bucket, count: b.count }))}
                margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
              >
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip
                  content={({ active, payload, label }) => {
                    if (!active || !Array.isArray(payload) || payload.length === 0) return null;
                    const p = payload[0];
                    const bucketName = p?.payload?.name ?? label ?? "—";
                    const count = p?.value ?? p?.payload?.count ?? 0;
                    return (
                      <Paper
                        elevation={0}
                        sx={{
                          px: 1.25,
                          py: 0.9,
                          bgcolor: theme.palette.background.paper,
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
                          boxShadow: theme.shadows[6],
                          minWidth: 180,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {bucketName}
                        </Typography>
                        <Box sx={{ mt: 0.6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            count
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                            {count}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  }}
                />
                <Bar dataKey="count" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Panel>

      <Panel title="Recent spans" subtitle="Filtered to this span name (max 500 loaded)">
        <CompGrid
          items={filteredSpans}
          loading={spansLoading}
          error={spansError}
          emptyMessage="No spans found for this selection"
          keyExtractor={(s) => `${s.trace_id}-${s.span_id}`}
          columns={columns}
        />
      </Panel>

      <Panel title="Status code breakdown">
        <CompGrid
          items={statusBreakdown.map(([code, count]) => ({ code, count }))}
          emptyMessage="No spans found."
          keyExtractor={({ code }) => String(code)}
          columns={[
            {
              id: "status",
              label: "Status",
              sortable: true,
              sortValue: ({ code }) => formatStatusCode(code),
              render: ({ code }) => (
                <Typography variant="body2">{formatStatusCode(code)}</Typography>
              ),
            },
            {
              id: "count",
              label: "Count",
              sortable: true,
              sortValue: ({ count }) => count,
              render: ({ count }) => (
                <Typography variant="body2">{count}</Typography>
              ),
            },
          ]}
        />
      </Panel>
    </Box>
  );
}
